import React, { createContext, useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import api from "../services/api";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const csrfResponse = await api.get("/api/get-csrf-token/");
        const token = csrfResponse.data.csrfToken || csrfResponse.headers["x-csrftoken"];
        // CSRF token is now stored in cookies, no need to set it manually

        const response = await api.get("/api/auth-status/");
        if (response.data.isAuthenticated) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  const login = async ({ username, password }) => {
    try {
      const response = await api.post("/api/login/", { username, password });
      if (response.status === 200) {
        setIsAuthenticated(true);
        navigate("/documents");
        return true;
      } else {
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const response = await api.post("/api/logout/");
      if (response.status === 200) {
        setIsAuthenticated(false);
        navigate("/authentication/sign-in");
      } else {
        console.error("Logout failed:", response);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
